import {CSSProperties} from 'react'
import {
  TooltipProps,
  createTheme,
  Button,
  ActionIcon,
  TextInput,
  Combobox,
  HoverCard,
  Chip,
  Loader
} from '@mantine/core'
import type {DefaultColors} from 'tailwindcss/types/generated/colors'
import type {RecursiveKeyValuePair} from 'tailwindcss/types/config'
import {mapValues, pick, pipe} from 'remeda'
import {match} from 'ts-pattern'
import {concat} from 'shared/collection'
import {fromTwColor} from 'shared/ui'
import tw from '../../../tailwind.config'
const twColors = tw.theme.colors as any as DefaultColors
if (!twColors) throw new Error('No tailwind colors found')
import textInputCss from './TextInput.module.css'

const primaryColor = 'blue' as const
export const colors = pipe(
  mapValues(
    pick(twColors, [
      'amber',
      'blue',
      'red',
      'teal',
      'yellow',
      'gray',
      'green',
      'indigo',
      'purple',
      'rose',
      'orange',
      'cyan',
      'emerald',
      'sky',
      'stone',
      'slate'
    ]),
    p => fromTwColor(p as RecursiveKeyValuePair)
  ),
  c => ({...c, primary: c[primaryColor]})
)

export const classes = {
  icon: {
    text: 'icon-text'
  },
  text: {
    bigBG: 'font-semibold text-gray-400 text-lg',
    bg: 'font-semibold text-gray-400',
    subtle: 'text-gray-400',
    label: '!text-gray-500 !text-sm font-semibold'
  },
  page: {
    textWidth: 'lg:w-text w-screen px-[2em]'
  }
}

export const styles = {
  container: {
    fullWidth: {position: 'relative', width: '99vw', padding: '2rem', left: 'calc(-50vw + 50%)'}
  }
} satisfies Record<string, Record<string, CSSProperties>>

export const appName = 'Swift'

export const theme = createTheme({
  primaryColor,
  colors: colors,
  white: twColors.white,
  black: twColors.black,
  fontFamily: 'var(--font-family)',
  lineHeights: {
    xs: '1.2',
    sm: '1.25',
    md: '1.35',
    lg: '1.45',
    xl: '1.5'
  },
  scale: 1,
  fontSizes: {
    xs: '0.8rem',
    sm: '0.9rem',
    md: '1rem',
    lg: '1.2rem'
  },
  focusRing: 'auto',
  shadows: {
    xs: '0 1px 3px rgba(0, 0, 0, 0.1)',
    sm: '0 1px 5px rgba(0, 0, 0, 0.1)',
    md: '0 0px 5px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.1)',
    lg: '0 0px 5px rgba(0, 0, 0, 0.1), 0 4px 24px rgba(0, 0, 0, 0.1)',
    xl: '0 0px 5px rgba(0, 0, 0, 0.1), 0 6px 32px rgba(0, 0, 0, 0.1)'
  },
  components: {
    Button: Button.extend({
      defaultProps: {
        variant: 'subtle'
      },
      vars: (_, p) =>
        match(p.variant)
          .with('outline', () => ({
            root: {
              '--button-color': 'var(--mantine-color-primary-filled)',
              '--button-bd': 'solid color-mix(in srgb, var(--mantine-color-text), transparent 90%) 1px',
              '--button-bg': 'transparent',
              '--button-radius': 'var(--mantine-radius-md)',
              '--button-padding-x-sm': '1em'
            },
            section: {
              '--mantine-spacing-xs': '0.3em'
            }
          }))
          .with('subtle', 'transparent', () => ({
            root: {
              '--button-color': 'color-mix(in srgb, var(--mantine-color-text), transparent 20%)',
              '--button-padding-x-sm': '0.7em'
            },
            section: {
              '--mantine-spacing-xs': '0.3em'
            }
          }))
          .otherwise(() => ({root: {}})),
      styles: {
        root: {overflow: undefined}
      }
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: 'subtle'
      },
      vars: (_, p) =>
        match(p.variant)
          .with('subtle', () => ({
            root: p.color ? {} : {'--ai-color': 'var(--mantine-color-gray-9)'}
          }))
          .otherwise(() => ({root: {}}))
    }),
    TextInput: TextInput.extend({
      defaultProps: {variant: 'filled'},
      classNames: {
        label: classes.text.label,
        ...textInputCss,
        input: concat(textInputCss.input as string, '!placeholder-gray-300').join(' ')
      },
      styles: {
        section: {width: 'fit-content'}
      }
    }),
    DateInput: {
      defaultProps: {variant: 'filled'}
    },
    InputWrapper: {
      classNames: {
        label: classes.text.label,
        input: '!placeholder-gray-300'
      }
    },
    DateTimePicker: {
      defaultProps: {variant: 'filled'}
    },
    Select: {
      defaultProps: {variant: 'filled'},
      classNames: {
        label: classes.text.label
      }
    },
    Autocomplete: {
      defaultProps: {variant: 'filled'},
      classNames: {
        label: classes.text.label
      }
    },
    MultiSelect: {
      defaultProps: {variant: 'filled'},
      classNames: {
        label: classes.text.label,
        searchInput: '!placeholder-gray-300'
      }
    },
    ColorInput: {
      defaultProps: {variant: 'filled'}
    },
    SegmentedControl: {
      classNames: {
        label: '!font-semibold !text-gray-400'
      }
    },
    Popover: {
      defaultProps: {
        shadow: 'md'
      },
      styles: {
        dropdown: {padding: '1rem'}
      }
    },
    Slider: {
      classNames: {
        markLabel: '!text-gray-400'
      }
    },
    Tooltip: {
      defaultProps: {
        withArrow: true,
        multiline: true,
        keepMounted: false
      } satisfies Partial<TooltipProps>
    },
    Chip: Chip.extend({
      defaultProps: {variant: 'filled'},
      vars: () => ({root: {'--chip-padding': '1em'}})
    }),
    Modal: {
      styles: {
        title: {
          fontWeight: 700,
          fontSize: '1.2em'
        }
      }
    },
    Combobox: Combobox.extend({
      defaultProps: {withinPortal: true, shadow: 'md', width: 'max-content', preventPositionChangeWhenVisible: false},
      styles: {
        dropdown: {maxWidth: '100vw'}
      }
    }),
    HoverCard: HoverCard.extend({
      defaultProps: {withinPortal: true},
      styles: {
        dropdown: {padding: '0.5em'}
      }
    }),
    Menu: {
      defaultProps: {
        shadow: 'md'
      }
    },
    PillsInput: {
      defaultProps: {
        variant: 'filled'
      }
    },
    Loader: Loader.extend({
      defaultProps: {
        type: 'dots'
      }
    }),
    Switch: {
      vars: () => ({root: {'--label-offset-start': '0.3em'}}),
      styles: {
        description: {marginTop: 2}
      }
    },
    Overlay: {
      defaultProps: {
        blur: 2,
        color: 'var(--mantine-color-body)',
        backgroundOpacity: 0.5
      },
      styles: {
        root: {
          'backdrop-filter': 'var(--overlay-filter)'
        }
      }
    }
  }
})
